/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from 'react';
import { Calculator, FileCheck2 } from 'lucide-react';
import { chamarApi } from '@/nucleo/auth/cliente-api';
import { Button } from '@/ui/base/button';
import { Input } from '@/ui/base/input';

const NS = '/bff/v1/j08-s01-calcular-composicao';

export function CalcularComposicaoPage() {
  const [estado, setEstado] = useState<any>();
  const [executando, setExecutando] = useState(false);
  const [regra, setRegra] = useState({
    valorDia: '100',
    valorAluno: '10',
    valorKm: '2',
    ajustePercentual: '5',
    retencaoPercentual: '11',
  });

  async function chamar(caminho: string, corpo?: any) {
    setExecutando(true);
    try {
      const envelope = await chamarApi<any>(NS + caminho, {
        metodo: caminho === '/state' ? 'GET' : 'POST',
        corpo,
      });
      setEstado(envelope.state);
    } finally {
      setExecutando(false);
    }
  }

  useEffect(() => {
    void chamar('/state');
  }, []);
  if (!estado)
    return (
      <main role="status" className="p-6">
        Carregando composições…
      </main>
    );

  const conjunto = estado.conjuntos[0];
  const publicar = () =>
    chamar('/verbs/publicar_regra', {
      tipoPessoa: conjunto.tipoPessoa,
      modalidade: conjunto.modalidade,
      vigenciaInicio: conjunto.competencia,
      valorDia: Number(regra.valorDia),
      valorAluno: Number(regra.valorAluno),
      valorKm: Number(regra.valorKm),
      ajustePercentual: Number(regra.ajustePercentual),
      retencaoPercentual: Number(regra.retencaoPercentual),
      escalaArredondamento: 2,
      chaveIdempotencia: `j08-s01-regra-${conjunto.tipoPessoa}-${conjunto.modalidade}-${conjunto.competencia}-v1`,
    });
  const calcular = (reexecutar = false) =>
    chamar('/verbs/calcular', {
      conjuntoId: conjunto.id,
      chaveIdempotencia: `j08-s01-calculo-${conjunto.id}-${reexecutar ? 'reexecucao' : 'inicial'}-v${reexecutar ? conjunto.composicoes.length + 1 : 1}`,
    });

  return (
    <main className="min-w-0 max-w-[calc(100vw-2rem)] space-y-5 overflow-hidden">
      <header>
        <h1 className="text-3xl font-semibold">
          <Calculator className="mr-2 inline h-7 w-7" />
          {estado.titulo}
        </h1>
        <p>{estado.descricao}</p>
      </header>
      {estado.alertas?.map((a: any, i: number) => (
        <div role="alert" className="rounded-md border border-destructive p-3" key={i}>
          {a.mensagem}
        </div>
      ))}
      {!conjunto && (
        <p className="rounded-md border border-dashed p-6">
          Nenhuma competência aprovada aguarda composição.
        </p>
      )}
      {conjunto && (
        <article className="space-y-4 rounded-md border p-4">
          <h2 className="font-semibold">
            <FileCheck2 className="mr-2 inline h-5 w-5" />
            {conjunto.protocolo} · {conjunto.competencia.slice(0, 7)}
          </h2>
          <p>
            OS {conjunto.ordemNumero} · {conjunto.modalidade} · credenciado {conjunto.tipoPessoa}
          </p>
          {!conjunto.regraVigente && (
            <section
              className="space-y-3 rounded-md bg-muted p-4"
              aria-label="Publicar tabela de preços"
            >
              <h3 className="font-medium">
                Regra sem vigência: publique os valores antes de calcular
              </h3>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
                {(
                  [
                    ['valorDia', 'Valor por dia'],
                    ['valorAluno', 'Valor por estudante'],
                    ['valorKm', 'Valor por km'],
                    ['ajustePercentual', 'Ajuste (%)'],
                    ['retencaoPercentual', 'Retenção (%)'],
                  ] as const
                ).map(([campo, rotulo]) => (
                  <label key={campo} className="text-sm">
                    {rotulo}
                    <Input
                      type="number"
                      step="0.01"
                      min={campo === 'ajustePercentual' ? -100 : 0}
                      value={regra[campo]}
                      onChange={(e) => setRegra({ ...regra, [campo]: e.target.value })}
                    />
                  </label>
                ))}
              </div>
              <Button disabled={executando} onClick={() => void publicar()}>
                Publicar regra vigente
              </Button>
            </section>
          )}
          {conjunto.regraVigente && (
            <div className="space-y-3">
              <p>
                Regra v{conjunto.regraVigente.versao}: dia R$ {conjunto.regraVigente.valorDia},
                estudante R$ {conjunto.regraVigente.valorAluno}, km R${' '}
                {conjunto.regraVigente.valorKm}.
              </p>
              <div className="flex flex-wrap gap-2">
                <Button
                  disabled={executando || conjunto.composicoes.length > 0}
                  onClick={() => void calcular(false)}
                >
                  Calcular composição
                </Button>
                <Button
                  variant="outline"
                  disabled={executando || conjunto.composicoes.length === 0}
                  onClick={() => void calcular(true)}
                >
                  Reexecutar mesmas entradas
                </Button>
              </div>
            </div>
          )}
          {conjunto.composicoes.map((c: any) => (
            <section
              key={c.id}
              className="space-y-2 rounded-md border p-3"
              aria-label="Memória de cálculo"
            >
              <h3 className="font-medium">
                Composição v{c.versao} · {c.status}
              </h3>
              <p>
                Dias R$ {c.resultado.baseDias} + estudantes R$ {c.resultado.baseAlunos} + km R${' '}
                {c.resultado.baseKm}
              </p>
              <p>
                Subtotal R$ {c.resultado.subtotal} · ajustes R$ {c.resultado.ajustes} · bruto R${' '}
                {c.resultado.bruto}
              </p>
              <p>
                Retenções R$ {c.resultado.retencoes} ·{' '}
                <strong>líquido R$ {c.resultado.liquido}</strong>
              </p>
              <p className="break-all font-mono text-xs">Hash {c.hashReproducao}</p>
            </section>
          ))}
        </article>
      )}
    </main>
  );
}
